import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react/jsx-runtime";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useTranslation } from "react-i18next";

interface ClaimDraftConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onYesClick: () => void;
  onNoClick: () => void;
}

export default function ClaimDraftConfirmation({
  isOpen,
  onClose,
  onYesClick,
  onNoClick,
}: ClaimDraftConfirmationProps) {
  const { t } = useTranslation();
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md z-[9999] space-y-5 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between text-gray-800 text-lg font-medium">
                  <Dialog.Title>{t("Continue Draft Claim")}</Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close modal"
                  >
                    <IoIosCloseCircleOutline
                      size={24}
                      className="text-gray-500 hover:text-red-500 transition"
                    />
                  </button>
                </div>

                {/* Message */}
                <p className="text-gray-600 text-base text-center">
                  {t(
                    "You already have a draft claim for this plan. Do you want to continue?"
                  )}
                </p>

                {/* Actions */}
                <div className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={onYesClick}
                    className="bg-brand-color hover:bg-brand-hover text-white font-semibold rounded-md py-2 px-4 disabled:bg-opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("Continue Draft Claim")}
                  </button>
                  <button
                    type="button"
                    onClick={onNoClick}
                    className="bg-transparent border border-red-500 text-red-600 hover:bg-red-600 hover:text-white font-semibold rounded-md py-2 px-4 transition-all disabled:bg-opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("Delete Draft & File New Claim")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
